import {unwrapResult} from '@reduxjs/toolkit';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  Col,
  UiBox,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiImage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {EMERGENCY_CONTRACEPTION_KEYS, METHOD_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification, useRequestState} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer, setUserQuestionnaireId} from '~/redux/questionnaires/slice';
import {addQuestionnaire} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {getCurrentUserFromStorage} from '~/services/auth';
import {spacing} from '~/utils/theme/spacing';
import {requestOK} from '~/utils/thunk-api';

const Questionnaire13Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const {accessToken: savedToken} = getCurrentUserFromStorage();
  const {addMessage, addValidationMessage} = useNotification(dispatch);

  const {loading} = useRequestState(addQuestionnaire);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[EMERGENCY_CONTRACEPTION_KEYS.OTHER_MEDICAL_HISTORY]?.data,
  );

  const medicalQuestionnaireAnswer = useSelector((state: RootState) => state.questionnaires?.collectData);

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const {wpAuthUser} = medicalQuestionnaire;

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === EMERGENCY_CONTRACEPTION_KEYS.OTHER_MEDICAL_HISTORY);

  const rules = {
    medicationsTaking: (attributes: any) => {
      return {
        presence: {allowEmpty: false, message: `^This field can't be blank`},
        length: {
          minimum: 5,
          tooShort: '^This field is too short (minimum is %{count} characters)',
          tokenizer: function (value: any) {
            return trimSpaces(value);
          },
        },
      };
    },
  };
  const {inputs, setInputs, validation, trimSpacesOnChange, trimSpaces} = useInput(
    {medicationsTaking: currentAnswer?.medicationsTaking || '', selectedOption: currentAnswer?.selectedOption || ''},
    {rules},
  );

  const renderQnsMedication = () => {
    if (inputs.selectedOption === 'YES') {
      return (
        <UiBox mt={spacing.medium} className="animate__animated animate__fadeInLeft">
          <UiText fontSize="md" fontWeight="bold">
            {questionnaire?.questions?.sub}
          </UiText>
          <UiFormControl id="medicationsTaking" isRequired isInvalid={!!validation?.errors?.medicationsTaking}>
            <UiTextarea
              mt={spacing.small}
              bg="white"
              borderRadius={spacing.tiny}
              placeholder="Type your response here..."
              value={inputs.medicationsTaking}
              onChange={({target}) => setInputs({medicationsTaking: target.value})}
              minH="200px"
            />
            {validation?.errors?.medicationsTaking && (
              <UiFormErrorMessage>{validation?.errors?.medicationsTaking[0]}</UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiBox>
      );
    }
    return null;
  };

  const onClickSubmit = async () => {
    if (inputs.selectedOption === 'YES') {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }

    const questionnaireAnswer = {
      key: EMERGENCY_CONTRACEPTION_KEYS.OTHER_MEDICAL_HISTORY,
      index: questionnaire?.index || '',
      data: inputs,
    };

    dispatch(setQuestionnaireAnswer(questionnaireAnswer));

    dispatch(setNextQuestionnaire({value: EMERGENCY_CONTRACEPTION_KEYS.PREFERRED_BRAND}));
  };

  return (
    <UiQuestionnaireLayout
      handleOnNext={onClickSubmit}
      actions={questionnaire?.actions}
      disableNext={!inputs.selectedOption}
      loading={loading}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.medium} />

        <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
          {questionnaire?.questions?.default}
        </UiText>
        {questionnaire?.descriptions?.default?.map(i => (
          <UiText key={i} fontSize="sm" mt={spacing.small}>
            {i}
          </UiText>
        ))}

        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid columns={2} spacing={5}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                onClick={() => setInputs({selectedOption: value.value})}
              />
            ))}
          </UiSimpleGrid>
          {renderQnsMedication()}
        </UiBox>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire13Screen;
